import { RiskWithFileResponse } from '@qcs/safety-client';
import { FC } from 'react';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { SectionHeader } from '../../../common/SectionHeader';
import { GridList } from '../../../common/grid/GridList';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { BASE_URL_WITH_VERSION } from '../../../../utils/api';
import { getMediaToken } from '../../../../utils/storage';
import { InnerTooltipWrapper, QcsTooltip } from '../../../common/basic/QcsTooltip';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAppSelector } from '../../../../store';
import { selectIdentity } from '../../../../store/entities/identity';
import { formatDate } from '../../../../utils/date';
import { getLangNameObj } from '../../../../utils/format';
import { hasRole } from '../../../../utils/roles';
import { useInvitation } from './invitationFunctions';
import { ShowImageButton } from './ShowImageButton';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitationState } from '@api/models/enums/InvitationState';
import { invitationService } from '@api/services/services';
import { resources } from '@api/resources';

interface Props {
  workplaceId?: string;
  onClose: () => void;
}

export const WorkplaceRiskModal: FC<Props> = ({ workplaceId, onClose }) => {
  const { t, i18n, invitation, callWithLock, reloadInvitation } = useInvitation();
  const mediaToken = getMediaToken();
  const identity = useAppSelector(selectIdentity);

  const workplace = invitation.workplaces?.find((x) => x.workplace?.id === workplaceId);

  const handleRenderData = (item: RiskWithFileResponse) => {
    const downloadDisabled = !item.file?.path;
    const approveDisabled =
      downloadDisabled ||
      !hasRole(identity.roles, [UserRole.ExternalManager, UserRole.ManagerWorkplace, UserRole.ManagerOsah]) ||
      invitation.state === InvitationState.Archived;
    const alreadyApproved = !!item.approvedOn;

    return (
      <>
        <QcsTableCell>{getLangNameObj(i18n, item.risk)}</QcsTableCell>
        <QcsTableCell>{alreadyApproved && `${item.approvedBy?.name ?? ''} | ${formatDate(i18n, item.approvedOn)}`}</QcsTableCell>
        <QcsTableCell align="right">
          <ShowImageButton
            tooltipTitle={t('invitation.tabs.tooltips.downloadRiskDocument')}
            documentUrl={`${BASE_URL_WITH_VERSION}${item.file?.path}?token=${mediaToken}`}
            disabled={downloadDisabled}
            color="success"
            fileName={item.file?.fileName}
          />

          <QcsTooltip title={t('invitation.tabs.tooltips.approveRisk')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton onClick={handleApprove(item)} disabled={approveDisabled || alreadyApproved} resourceLoading={resources.approveRisks}>
                <CheckCircleIcon color={approveDisabled ? undefined : alreadyApproved ? 'success' : 'warning'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
        </QcsTableCell>
      </>
    );
  };

  const handleApprove = (item: RiskWithFileResponse) => async () => {
    await callWithLock(async () => {
      await invitationService.approveRisks(invitation.id!, {
        workplacesWithRisks: {
          [workplace!.workplace!.id]: [item.risk!.id!],
        },
      });
      await reloadInvitation();
    });
  };

  return (
    <QcsModal open={!!workplace} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title="invitation.workplace.riskTitle" />
        <GridList<RiskWithFileResponse>
          headers={[{ captionStr: 'invitation.workplace.riskName' }, { captionStr: 'supplierEmployeeDocument.approvedBy' }, {}]}
          data={workplace?.risks ?? []}
          renderData={handleRenderData}
          search={false}
          hidePagination={true}
        />
      </QcsModalBox>
    </QcsModal>
  );
};
